import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  FilterAlt as FilterAltIcon,
  ArrowDropDown as ArrowDropDownIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import { searchableSelectAutocompleteProps } from '../../../components/SearchableSelect';
import ClienteInlineSearchField from '../../../components/Tesoreria/ClienteInlineSearchField';
import ProveedorInlineSearchField from '../../../components/ProveedorInlineSearchField';
import type { McpOption, SucursalBanco, VencimientoRapido } from './types';
import { calcularRangoVencimientoRapido } from '../../../utils/chequeTerceroDisplay';

export interface ChequesTerceroFiltrosPanelProps {
  isMobile: boolean;
  vistaCompacta: boolean;
  onVistaCompactaChange: (v: boolean) => void;
  estado: string;
  onEstadoChange: (v: string) => void;
  vencimientoRapido: VencimientoRapido;
  onVencimientoRapidoChange: (v: VencimientoRapido) => void;
  nroCliente: number | null;
  nombreCliente: string;
  onClienteSelect: (nro: number, nombre: string) => void;
  onOpenClienteSearch: () => void;
  bancoNombre: string;
  onOpenBancoCheque: () => void;
  nroSucursal: string;
  onNroSucursalChange: (v: string) => void;
  sucursalesFiltradas: SucursalBanco[];
  nroBanco: string;
  nroProveedor: number | null;
  nombreProveedor: string;
  onProveedorSelect: (nro: number, nombre: string) => void;
  onOpenProveedorSearch: () => void;
  bancoDepositoNombre: string;
  onOpenBancoDeposito: () => void;
  nroCuentaDeposito: string;
  onNroCuentaDepositoChange: (v: string) => void;
  fechaIngresoDesde: string;
  fechaIngresoHasta: string;
  onFechaIngresoDesde: (v: string) => void;
  onFechaIngresoHasta: (v: string) => void;
  fechaSalidaDesde: string;
  fechaSalidaHasta: string;
  onFechaSalidaDesde: (v: string) => void;
  onFechaSalidaHasta: (v: string) => void;
  fechaVencimientoDesde: string;
  fechaVencimientoHasta: string;
  onFechaVencimientoDesde: (v: string) => void;
  onFechaVencimientoHasta: (v: string) => void;
  importeDesde: string;
  importeHasta: string;
  onImporteDesde: (v: string) => void;
  onImporteHasta: (v: string) => void;
  mcp: string;
  listaMcps: McpOption[];
  onMcpChange: (v: string) => void;
  filtroTipoSoporte: string;
  onFiltroTipoSoporteChange: (v: string) => void;
  onClearFilters: () => void;
}

const GrupoTitulo: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Grid item xs={12}>
    <Typography variant="subtitle2" color="primary" sx={{ mt: 1, fontWeight: 600 }}>
      {children}
    </Typography>
  </Grid>
);

const ChequesTerceroFiltrosPanel: React.FC<ChequesTerceroFiltrosPanelProps> = (props) => {
  const handleVencimientoRapido = (val: VencimientoRapido) => {
    props.onVencimientoRapidoChange(val);
    if (!val) {
      props.onFechaVencimientoDesde('');
      props.onFechaVencimientoHasta('');
      return;
    }
    const rango = calcularRangoVencimientoRapido(val);
    if (rango) {
      props.onFechaVencimientoDesde(rango.desde);
      props.onFechaVencimientoHasta(rango.hasta);
    }
  };

  return (
    <>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'flex-end' }}
        sx={{ mb: 2 }}
      >
        <TextField
          select
          fullWidth
          label="Estado"
          value={props.estado}
          onChange={(e) => props.onEstadoChange(e.target.value)}
          size="small"
          sx={{ width: { md: 160 }, flexShrink: 0 }}
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="en_cartera">En cartera</MenuItem>
          <MenuItem value="entregado">Entregado</MenuItem>
          <MenuItem value="conciliado">Conciliado</MenuItem>
          <MenuItem value="rechazado">Rechazado</MenuItem>
          <MenuItem value="anulado">Anulado</MenuItem>
        </TextField>

        <TextField
          select
          fullWidth
          label="Soporte"
          value={props.filtroTipoSoporte}
          onChange={(e) => props.onFiltroTipoSoporteChange(e.target.value)}
          size="small"
          sx={{ width: { md: 160 }, flexShrink: 0 }}
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="fisicos">Físicos</MenuItem>
          <MenuItem value="electronico">Electrónicos</MenuItem>
        </TextField>

        <Box sx={{ flex: { md: '1 1 280px' }, minWidth: { md: 220 } }}>
          <ClienteInlineSearchField
            label="Cliente"
            placeholder="Número, nombre o CUIT"
            size="small"
            helperText=""
            excluirInactivos={false}
            displayValue={
              props.nroCliente != null ? `${props.nombreCliente} - ${props.nroCliente}` : ''
            }
            onSelect={(c) => props.onClienteSelect(c.nroCliente, c.nombre ?? '')}
            onOpenModal={props.onOpenClienteSearch}
          />
        </Box>

        <TextField
          select
          fullWidth
          label="Vencimiento"
          value={props.vencimientoRapido}
          onChange={(e) => handleVencimientoRapido(e.target.value as VencimientoRapido)}
          size="small"
          sx={{ width: { md: 180 }, flexShrink: 0 }}
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="este_mes">Vencen este mes</MenuItem>
          <MenuItem value="proximos_7">Próximos 7 días</MenuItem>
          <MenuItem value="vencidos">Vencidos</MenuItem>
        </TextField>

        <Box sx={{ width: { md: 200 }, flexShrink: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            component="label"
            sx={{ display: 'block', mb: 0.75, ml: 0.5, lineHeight: 1 }}
          >
            Vista
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={props.vistaCompacta ? 'compacta' : 'completa'}
            onChange={(_, val) => {
              if (val) props.onVistaCompactaChange(val === 'compacta');
            }}
            aria-label="Vista del listado"
            sx={{
              '& .MuiToggleButton-root': {
                flex: 1,
                py: 0.85,
                textTransform: 'none',
                fontSize: '0.8125rem',
              },
            }}
          >
            <ToggleButton value="compacta">Compacta</ToggleButton>
            <ToggleButton value="completa">Completa</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Stack>

      <Accordion sx={{ mb: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#f9f9f9' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FilterAltIcon sx={{ color: '#1976d2' }} />
            <Typography variant="h6" sx={{ color: '#1976d2', fontSize: '1rem' }}>
              Más filtros
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <GrupoTitulo>Quién</GrupoTitulo>
            <Grid item xs={12} sm={6} md={4}>
              <ProveedorInlineSearchField
                label="Proveedor"
                placeholder="Número, nombre o CUIT"
                displayValue={
                  props.nroProveedor != null
                    ? `${props.nombreProveedor} - ${props.nroProveedor}`
                    : ''
                }
                onSelect={(p) => props.onProveedorSelect(p.nroProveedor, p.nombre ?? '')}
                onOpenModal={props.onOpenProveedorSearch}
              />
            </Grid>

            <GrupoTitulo>Dónde</GrupoTitulo>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Banco del cheque"
                value={props.bancoNombre}
                placeholder="Seleccionar..."
                size="small"
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <ArrowDropDownIcon
                        sx={{ cursor: 'pointer' }}
                        onClick={props.onOpenBancoCheque}
                      />
                    </InputAdornment>
                  ),
                }}
                onClick={props.onOpenBancoCheque}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Sucursal"
                value={props.nroSucursal}
                onChange={(e) => props.onNroSucursalChange(e.target.value)}
                disabled={!props.nroBanco}
                size="small"
              >
                <MenuItem value="">Todas</MenuItem>
                {props.sucursalesFiltradas.map((s) => (
                  <MenuItem key={s.nroSucursal} value={s.nroSucursal.toString()}>
                    {s.sucursal}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Banco depósito"
                value={props.bancoDepositoNombre}
                placeholder="Seleccionar..."
                size="small"
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <ArrowDropDownIcon
                        sx={{ cursor: 'pointer' }}
                        onClick={props.onOpenBancoDeposito}
                      />
                    </InputAdornment>
                  ),
                }}
                onClick={props.onOpenBancoDeposito}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                type="number"
                fullWidth
                label="Nro. cuenta depósito"
                value={props.nroCuentaDeposito}
                onChange={(e) => props.onNroCuentaDepositoChange(e.target.value)}
                size="small"
                inputProps={{ min: 0 }}
              />
            </Grid>

            <GrupoTitulo>Cuándo</GrupoTitulo>
            {[
              ['Fecha ingreso desde', props.fechaIngresoDesde, props.onFechaIngresoDesde],
              ['Fecha ingreso hasta', props.fechaIngresoHasta, props.onFechaIngresoHasta],
              ['Fecha salida desde', props.fechaSalidaDesde, props.onFechaSalidaDesde],
              ['Fecha salida hasta', props.fechaSalidaHasta, props.onFechaSalidaHasta],
              ['Fecha venc. desde', props.fechaVencimientoDesde, props.onFechaVencimientoDesde],
              ['Fecha venc. hasta', props.fechaVencimientoHasta, props.onFechaVencimientoHasta],
            ].map(([label, value, onChange]) => (
              <Grid item xs={12} sm={6} md={3} key={String(label)}>
                <TextField
                  type="date"
                  fullWidth
                  label={label as string}
                  value={value as string}
                  onChange={(e) => (onChange as (v: string) => void)(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
            ))}

            <GrupoTitulo>Cuánto / Medio</GrupoTitulo>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                type="number"
                fullWidth
                label="Monto desde"
                value={props.importeDesde}
                onChange={(e) => props.onImporteDesde(e.target.value)}
                size="small"
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                type="number"
                fullWidth
                label="Monto hasta"
                value={props.importeHasta}
                onChange={(e) => props.onImporteHasta(e.target.value)}
                size="small"
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                {...searchableSelectAutocompleteProps}
                options={[{ mcp: '', descripcion: 'Todos' }, ...props.listaMcps]}
                getOptionLabel={(option) =>
                  option.mcp
                    ? option.descripcion
                      ? `${option.mcp} - ${option.descripcion}`
                      : option.mcp
                    : 'Todos'
                }
                value={
                  props.mcp
                    ? props.listaMcps.find((o) => o.mcp === props.mcp) ?? {
                        mcp: props.mcp,
                        descripcion: props.mcp,
                      }
                    : { mcp: '', descripcion: 'Todos' }
                }
                onChange={(_, newValue) => props.onMcpChange(newValue?.mcp ?? '')}
                isOptionEqualToValue={(opt, val) => opt.mcp === val.mcp}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="MCP"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <AccountBalanceIcon sx={{ color: '#666', fontSize: 20 }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Button variant="outlined" onClick={props.onClearFilters} size="small">
                Limpiar filtros
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default ChequesTerceroFiltrosPanel;
